import { gql } from '@apollo/client';

export const SPECIMEN_FRAGMENT = gql`
fragment specimanFragment on Speciman {
  __typename
  id
  ageYears
  name
  code
  slug
  bioregionGid
  biodiversityRichness
  biodiversityRichnessMicrohabitats
  condition
  crownLightExposure
  learningPath {
    id
    name
  }
  osmTags
  territoryId
  territory {
    name
    slug
    id
    isCurrentUserMentor
    avatarUrl
  }
  territoriesByCoordinatesGeometry {
    nodes {
      id
      slug
    }
  }
  coordinates { x, y }
  cultureDescription
  district
  girthCm
  surfaceSealing
  notes
  condition
  coverPictureUrl
  coverPictureWidth
  coverPictureHeight
  natureDescription
  lastMeasuredAt
  isRestingInPeace
  isNatureMonument
  itreeApiJson
  speciesId
  type
  createdAt
  createdByUserId
  createdByUser {
    id
    name
    totalCredits
    isTerritoryMentor
    username
    avatarUrl
  }
  updatedAt
  reviewerId
    reviewer {
      id
      name
      totalCredits
      isTerritoryMentor
      username
      avatarUrl
    }
  status
  currentUserHasObserved
  species {
    __typename
    id
    slug
    name
    scientificName
    family
    treeType
    barkPictureUrl
    barkType
    leafPictureUrl
    leafType
    leafShape
    leafMargin
    needleType
    coneType
    conePictureUrl
  }
  updatedByUser {
    id
    name
  }
  specimenUploadedResourcesBySpecimenId {
    __typename
    nodes {
    __typename
      id
      name
      uploadedResourceId
      type
      uploadedResource {
        id
        url
      }
    }
  }
  specimenAttachmentsBySpecimenId(
    orderBy: POSITION_ASC
    filter: {type: {equalTo: IMAGE}}
  ) {
    nodes {
      id
      url
      position
    }
  }
  routeStopsBySpecimenId {
    nodes {
      id
      route {
        id
        name
      }
    }
  }
 }`;

export const SPECIMEN_BY_SLUG = gql`
${SPECIMEN_FRAGMENT}
query specimanBySlug($slug: String!) {
  specimanBySlug(slug: $slug) {
    ...specimanFragment
  }
}`;

export const UPDATE_SPECIMEN = gql`
${SPECIMEN_FRAGMENT}
mutation updateSpecimanBySlug(
  $name: String
  $slug: String!
  $code: String
  $speciesId: UUID
  $learningPathId: UUID
  $osmTags: String
  $coverPictureUrl: JSON
  $coverPictureWidth: Int
  $coverPictureHeight: Int
  $type: SpecimenType!
  $status: SpecimenSpeciesStatus
) {
  updateSpecimanBySlug(
    input: {
      patch: {
        name: $name
        code: $code
        speciesId: $speciesId
        learningPathId: $learningPathId
        osmTags: $osmTags
        slug: $slug
        coverPictureUrl: $coverPictureUrl
        coverPictureWidth: $coverPictureWidth
        coverPictureHeight: $coverPictureHeight
        type: $type
        status: $status
      }
      slug: $slug
    }
  ) {
    clientMutationId
    speciman {
      ...specimanFragment
    }
  }
}`;

export const CREATE_SPECIMEN = gql`
${SPECIMEN_FRAGMENT}
mutation CreateSpeciman(
  $coverPictureUrl: JSON
  $coverPictureWidth: Int
  $coverPictureHeight: Int
  $name: String!
  $speciesId: UUID
  $learningPathId: UUID
  $osmTags: String
  $code: String
  $type: SpecimenType!
  $territoryId: UUID
  $bioregionGid: Int
) {
  createSpeciman(
    input: {
      speciman: {
        speciesId: $speciesId
        learningPathId: $learningPathId
        osmTags: $osmTags
        type: $type
        coverPictureUrl: $coverPictureUrl
        coverPictureWidth: $coverPictureWidth
        coverPictureHeight: $coverPictureHeight
        code: $code
        name: $name
        territoryId: $territoryId
        bioregionGid: $bioregionGid
      }
    }
  ) {
    speciman {
      ...specimanFragment
    }
  }
}`;

export const CREATE_SPECIMEN_ATTACHMENT = gql`
${SPECIMEN_FRAGMENT}
mutation createSpecimenAttachment(
  $position: Int!
  $specimenId: UUID!
  $url: String!
) {
  createSpecimenAttachment(
    input: {
      specimenAttachment: {
        specimenId: $specimenId
        position: $position
        url: $url
        type: IMAGE
      }
    }
  ) {
    specimen {
      ...specimanFragment
    }
  }
}
`;

export const UPDATE_SPECIMEN_ATTACHMENT = gql`
${SPECIMEN_FRAGMENT}
mutation updateSpecimenAttachment($id: UUID!, $position: Int!) {
  updateSpecimenAttachment(input: { patch: { position: $position }, id: $id }) {
    specimen {
      ...specimanFragment
    }
  }
}
`;

export const DELETE_SPECIMEN_ATTACHMENT = gql`
${SPECIMEN_FRAGMENT}
mutation deleteSpecimenAttachment($id: UUID!) {
  deleteSpecimenAttachment(input: {id: $id}) {
    specimen {
      ...specimanFragment
    }
  }
}
`;

export const CREATE_RESOURCE = gql`
  mutation CreateResource($url: String!) {
    createUploadedResource(input: { uploadedResource: { url: $url } }) {
      uploadedResource {
	url
	id
      }
    }
  }
`;

export const CREATE_SPECIMEN_UPLOADED_RESOURCE = gql`
  mutation createSpecimenUploadedResource(
    $specimenId: UUID!
    $uploadedResourceId: UUID!
    $name: JSON
    $type: SpeciesSpecimenUploadedResourceType
  ) {
    createSpecimenUploadedResource(
      input: {
	specimenUploadedResource: {
	  specimenId: $specimenId
	  uploadedResourceId: $uploadedResourceId
	  name: $name
	  type: $type
	}
      }
    ) {
      specimen {
	id
      }
      specimenUploadedResource {
	id
	name
	uploadedResourceId
	uploadedResource {
          id
          url
	}
      }
    }
  }
`;

export const DELETE_SPECIMEN_UPLOADED_RESOURCE = gql`
mutation deleteSpecimenUploadedResource($uploadedResourceId: UUID!) {
  deleteSpecimenUploadedResource(input: { id: $uploadedResourceId }) {
    clientMutationId
  }
}
`;

export const UPDATE_SPECIMEN_UPLOADED_RESOURCE = gql`
mutation updateSpecimenUploadedResource($id: UUID!, $name: JSON) {
  updateSpecimenUploadedResource(input: { patch: { name: $name }, id: $id }) {
    specimenUploadedResource {
      id
      name
    }
  }
}
`;
